import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/common/Logo";
import { ShieldCheck, Loader2 } from "lucide-react";

export default function Login() {
  const { user, login, formatApiError } = useAuth();
  const [email, setEmail] = useState("admin@corebridge.net");
  const [password, setPassword] = useState("Admin@12345");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await login(email, password);
      nav("/dashboard");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[var(--bg)] grain">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden border-r border-[var(--border)]">
        <div className="absolute inset-0 bg-cover bg-center opacity-30"
             style={{ backgroundImage: "url('https://images.unsplash.com/photo-1708779493105-9c743e367a3c?crop=entropy&cs=srgb&fm=jpg&q=85')" }}/>
        <div className="absolute inset-0 bg-gradient-to-br from-black via-transparent to-black opacity-90"/>
        <div className="relative p-16 flex flex-col justify-between w-full">
          <div className="flex items-center gap-3">
            <Logo size={40}/>
            <div className="leading-none">
              <div className="text-lg font-semibold tracking-tight">CoreBridge</div>
              <div className="text-[10px] text-[var(--text-3)] uppercase tracking-[0.2em] mt-1">Technologies</div>
            </div>
          </div>
          <div>
            <h1 className="text-5xl font-bold tracking-tight leading-tight max-w-md">
              Integrate <span className="text-[var(--gold)]">once</span>.
              <br/>Access every provider.
            </h1>
            <p className="text-[var(--text-2)] mt-4 max-w-sm text-base">
              The unified B2B aggregation infrastructure between casino operators and game providers — wallet, transactions, settlement, reconciliation in a single platform.
            </p>
            <div className="mt-10 flex gap-6 text-xs text-[var(--text-3)] font-mono">
              <div><span className="text-[var(--gold)]">99.99%</span> uptime</div>
              <div><span className="text-[var(--gold)]">50ms</span> p95 latency</div>
              <div><span className="text-[var(--gold)]">5+</span> providers</div>
            </div>
          </div>
          <div className="text-[10px] text-[var(--text-3)] uppercase tracking-widest">
            ⓘ Production-grade B2B infrastructure — not a player-facing service
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="login-form">
          <div className="flex items-center gap-2 mb-8">
            <ShieldCheck className="text-[var(--gold)]" size={18}/>
            <span className="text-xs text-[var(--text-2)] uppercase tracking-widest">Secure Console</span>
          </div>
          <h2 className="text-3xl font-semibold mb-2">Sign in</h2>
          <p className="text-[var(--text-2)] text-sm mb-8">Access the aggregator control plane.</p>

          <label className="block text-xs uppercase tracking-widest text-[var(--text-3)] mb-1.5">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2.5 bg-[var(--surface)] border border-[var(--border)] rounded text-sm focus:outline-none focus:border-[var(--gold)]"
            data-testid="login-email-input"
            required
          />
          <label className="block text-xs uppercase tracking-widest text-[var(--text-3)] mb-1.5 mt-5">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2.5 bg-[var(--surface)] border border-[var(--border)] rounded text-sm focus:outline-none focus:border-[var(--gold)]"
            data-testid="login-password-input"
            required
          />
          {err && <div className="mt-4 text-sm text-[var(--error)]" data-testid="login-error">{err}</div>}
          <button
            type="submit"
            disabled={busy}
            className="btn-gold w-full py-2.5 rounded mt-6 text-sm flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="login-submit-btn"
          >
            {busy ? <Loader2 className="animate-spin" size={14}/> : null} Sign in
          </button>
          <div className="mt-6 text-xs text-[var(--text-3)] font-mono">
            Demo: admin@corebridge.net · Admin@12345
          </div>
        </form>
      </div>
    </div>
  );
}
